import { supabase } from '@/integrations/supabase/client';
import { BaseService } from './BaseService';
import { logger } from '@/utils/structuredLogging';

export type NotificationType = 
  | 'thread_reply'
  | 'mention'
  | 'xp_gift'
  | 'badge_earned'
  | 'connection_request'
  | 'dm_message'
  | 'streak_milestone'
  | 'weekly_goal_met'
  | 'thread_featured';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, any> | null;
  read: boolean;
  created_at: string;
}

class InAppNotificationService extends BaseService {
  constructor() {
    super('InAppNotificationService');
  }

  async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    data?: Record<string, any>
  ): Promise<Notification> {
    return this.execute(async () => {
      logger.info('Creating notification', {
        component: this.serviceName,
        metadata: { userId, type, title }
      });

      const { data: notification, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type,
          title,
          message,
          data: data || null,
          read: false
        })
        .select()
        .single();

      if (error) throw error;

      return notification as Notification;
    });
  }

  async getNotifications(
    userId: string,
    limit: number = 20,
    unreadOnly: boolean = false
  ): Promise<Notification[]> {
    return this.execute(async () => {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (unreadOnly) {
        query = query.eq('read', false);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as Notification[];
    });
  }

  async markAsRead(notificationId: string): Promise<void> {
    return this.execute(async () => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;
    });
  }

  async markAllAsRead(userId: string): Promise<void> {
    return this.execute(async () => {
      logger.info('Marking all notifications as read', {
        component: this.serviceName,
        metadata: { userId }
      });

      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
    });
  }

  async getUnreadCount(userId: string): Promise<number> {
    return this.execute(async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('read', false);

      if (error) throw error;
      return count || 0;
    });
  }

  async deleteNotification(notificationId: string): Promise<void> {
    return this.execute(async () => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    });
  }

  subscribeToNotifications(
    userId: string,
    callback: (notification: Notification) => void
  ) {
    const channel = supabase
      .channel(`notifications:${userId}:${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          logger.info('New notification received', {
            component: this.serviceName,
            metadata: { notificationId: payload.new.id }
          });
          callback(payload.new as Notification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }
}

export const inAppNotificationService = new InAppNotificationService();
